import { Link } from "react-router-dom";

import GoldenRod from "./GoldenRod";
import Icon from "./Icon";
import ExpandedInfo from "./ExpandInfo";
import T from "./translations/Translations";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AppButton {
    label: string;
    href?: string | null;
    link?: string | null;
    clickIt?: boolean;
}

interface Application {
    app: string;
    body?: string | null;
    broadcast?: {
        description: string;
        linkText: string;
        url: string;
    } | null;
    buttons?: AppButton[] | null;
    endDate?: string | null;
    icon: string;
    notice?: string[] | null;
    startDate?: string | null;
    status?: string | null;
    statusColor?: string;
    statusNumber?: number | null;
    subBody?: string | null;
    subBodyRange?: string | null;
    subBody2?: string | null;
    subBody2Range?: string | null;
    subBody3?: string | null;
    subBody3Range?: string | null;
    subStatus?: string | null;
    subStatusRange?: string | null;
    title: string;
}

interface ApplicationBlockProps {
    application: Application | null;
    expand?: boolean;
    lang?: string;
    setExpand?: ((val: boolean) => void) | null;
    student?: Record<string, unknown> | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Display an application block containing its relevant information
 * @name ApplicationBlock
 * @param {ApplicationBlockProps} props
 * @returns {JSX.Element | null}
 */
const ApplicationBlock = ({
    application,
    expand = false,
    lang = "en",
    setExpand = null,
    student = null
}: ApplicationBlockProps): JSX.Element | null => {
    if (!application) return null;

    const {
        body,
        broadcast,
        buttons,
        endDate,
        notice,
        startDate,
        status,
        statusColor,
        statusNumber,
        subBody,
        subBodyRange,
        subBody2,
        subBody2Range,
        subStatus,
        subStatusRange,
        title,
        icon
    } = application;

    const newSubBody: React.ReactNode = subBody ? T({ tKey: subBody ?? "", langStr: lang }) : null;

    return (
        <div className="application fade-style">
            <div className="application-header">
                <div className="left">
                    <div className="icon-title">
                        <div className="icon-container" style={{ background: "#808080" }}>
                            <Icon fill="white" height={65} iconName={icon} title={title} width={60} />
                        </div>
                    </div>
                    <div className="title-container">
                        {T({ tKey: title, langStr: lang })}
                        <div className="app-buttons-container">
                            {buttons?.map((buttonObj: AppButton, idx: number) => {
                                const buttonKey = `app-${application.app}-button-${idx}`;
                                const { clickIt, href, label, link } = buttonObj;
                                return (
                                    <div key={buttonKey}>
                                        {href && (
                                            <a href={href}>
                                                <button className="app-buttons">
                                                    {T({ tKey: label, langStr: lang })}
                                                </button>
                                            </a>
                                        )}
                                        {link && (
                                            <Link to={link}>
                                                <button className="app-buttons">
                                                    {T({ tKey: label, langStr: lang })}
                                                </button>
                                            </Link>
                                        )}
                                        {clickIt && (
                                            <button className="app-buttons" onClick={() => setExpand?.(true)}>
                                                {T({ tKey: label, langStr: lang })}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div>
                    <div className="status-and-dates">
                        <div className="status">
                            <span className="label">
                                {status && <>{T({ tKey: "Global_Status", langStr: lang })}:</>}
                            </span>{" "}
                            <span className="info" style={{ color: statusColor }}>
                                {statusNumber || ""} {T({ tKey: status ?? "", langStr: lang })}
                            </span>
                        </div>
                        {subStatus && (
                            <div className="sub-status">
                                {T({ tKey: subStatus, langStr: lang })} {subStatusRange || ""}
                            </div>
                        )}
                        {(startDate || endDate) && (
                            <div className="dates">
                                <span className="label">{T({ tKey: "Global_OpenFrom", langStr: lang })}: </span>
                                <span>
                                    {startDate}
                                    {startDate && endDate && " - "}
                                    {endDate}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <hr />
            <div className="application-body">
                {notice && (
                    <div>
                        <div className="notice">
                            <i className="bi bi-exclamation-triangle" />{" "}
                            {notice.length > 2 ? (
                                <>
                                    {T({ tKey: notice[0], langStr: lang })} {notice[1]}.{" "}
                                    {T({ tKey: notice[2], langStr: lang })}
                                </>
                            ) : (
                                T({ tKey: notice[0], langStr: lang })
                            )}
                        </div>
                    </div>
                )}
                {body && <div>{T({ tKey: body, langStr: lang })}</div>}
                {subBody && (
                    <div className="sub-body">
                        {newSubBody} {subBodyRange || ""}
                    </div>
                )}
                {subBody2 && (
                    <div className="sub-body">
                        {T({ tKey: subBody2, langStr: lang })} {subBody2Range || ""}
                    </div>
                )}
                {broadcast && (
                    <GoldenRod
                        bodyText={
                            <div className="survey-content">
                                {broadcast.url && broadcast.linkText && (
                                    <a
                                        className="survey-button"
                                        href={broadcast.url}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                    >
                                        {T({ tKey: broadcast.linkText, langStr: lang })}
                                    </a>
                                )}
                                <p>{T({ tKey: broadcast.description, langStr: lang })}</p>
                            </div>
                        }
                    />
                )}
                {expand && student && <ExpandedInfo setExpand={setExpand} student={student} />}
            </div>
        </div>
    );
};

export default ApplicationBlock;
